import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FormikPremiumInput } from 'modules/emergencies/components/ui/inputs/customInput';
import { SectionContainer } from '../../ticketForm/components/SectionContainer';
import SectionTitle from '../../ticketForm/components/SectionTitle';
import SectionSubTitle from '../../ticketForm/components/SectionSubTitle';
import { SectionBody } from '../../ticketForm/components/SectionBody';
import { SectionLine } from '../../ticketForm/components/SectionLine';
import { CustomSpace } from '../../ticketForm/components/CustomSpace';
//styles
import { QuoteBidSection } from './QuoteBidSection';
import { useSelector } from 'react-redux';
import { constants, getSpecificClientConfig } from 'redux/modules/client-config/selectors';
import useGetBids from '../../modals/useGetBids';
import useCarriersFilter from '../../ticketForm/useCarriersFilter';
import ticketService from 'modules/emergencies/ticketService';

const t = {
  section_title: 'Supply info',
  cost_subtitle: 'Cost options',
  carrier_subtitle: 'Assigned carrier',
  no_bids: 'No reply has been received from the carriers yet',
};

export const QuoteComplementaryInfoSection = ({ quoteFormBodyData }) => {
  const carrierSelector = useSelector((store) =>
    getSpecificClientConfig(store, constants.EMERGENCIES_CARRIER_SELECTOR)
  );
  const showBidsEnabled = useSelector((store) =>
    getSpecificClientConfig(store, constants.EMERGENCIES_BIDS)
  );
  const ticketHasOpenAuctions = ticketService.bussiness.ticketHasOpenAuctions(
    quoteFormBodyData.ticketDetail
  );
  const showBids = showBidsEnabled && ticketHasOpenAuctions;

  const { showErrors, ticketDetail, isEdit, setFieldValue, values } = quoteFormBodyData;
  const { bids, isLoadingTicket } = useGetBids(ticketDetail?.id);

  const initialCarrierQuery = '';

  const { carrierOptions, carrierOptionsError, isLoadingCarrierOptions, setSearch } =
    useCarriersFilter({
      initialQuery: initialCarrierQuery,
    });

  const existingCarrier = ticketDetail?.quote?.quote_costs?.[0]?.carrier || null;
  const newCostSchema =
    (!!ticketDetail?.quote?.quote_costs?.length > 0 || !isEdit) && !!ticketDetail.cargo;

  const carrierOptionsWithExistingCarrier = [...carrierOptions];
  const existingCarrierIsAlreadyInOptions = carrierOptions.some(
    (option) => option.value === existingCarrier?.id
  );

  if (!!existingCarrier && !existingCarrierIsAlreadyInOptions) {
    carrierOptionsWithExistingCarrier.push({
      value: existingCarrier.id,
      label: existingCarrier.name,
      display: existingCarrier.name,
      search: existingCarrier.name,
    });
  }

  const initialBid = ticketDetail?.quote?.quote_costs?.[0]?.bid || null;

  const [selectedBid, setSelectedBid] = useState(initialBid);

  const isSelectedBidEqualToFormCost = useMemo(() => {
    if (!selectedBid) return false;
    try {
      const bidCarrierId = selectedBid.bidder.carrier.id;
      const bidAmount = parseFloat(selectedBid.cost.amount);
      const bidCurrency = selectedBid.cost.currency;

      const formCarrierId = values.quote_costs[0]?.carrier.id;
      const formAmount = parseFloat(values.quote_costs[0]?.cost.amount);
      const formCurrency = values.quote_costs[0]?.cost.currency;

      return (
        bidCarrierId === formCarrierId && bidAmount === formAmount && bidCurrency === formCurrency
      );
    } catch (e) {
      return false;
    }
  }, [values, selectedBid]);

  const setSelectedBidFromForm = useCallback(() => {
    try {
      if (!showBidsEnabled) return;
      const formCarrierId = values.quote_costs[0]?.carrier.id;
      const formAmount = parseFloat(values.quote_costs[0]?.cost.amount);
      const formCurrency = values.quote_costs[0]?.cost.currency;

      const matchingBid = bids.find((bid) => {
        const bidCarrierId = bid.bidder.carrier.id;
        const bidAmount = parseFloat(bid.cost.amount);
        const bidCurrency = bid.cost.currency;
        return (
          bidCarrierId === formCarrierId && bidAmount === formAmount && bidCurrency === formCurrency
        );
      });

      if (matchingBid) {
        setSelectedBid(matchingBid);
        if (newCostSchema) setFieldValue('quote_costs[0].bid_id', matchingBid.id);
      }
    } catch (e) {
      console.error(e);
    }
  }, [bids, newCostSchema, setFieldValue, showBidsEnabled, values.quote_costs]);

  useEffect(() => {
    if (!selectedBid) {
      setSelectedBidFromForm();
    }
  }, [selectedBid, setSelectedBidFromForm]);

  useEffect(() => {
    if (!isSelectedBidEqualToFormCost) {
      setSelectedBid(null);
      if (newCostSchema) setFieldValue('quote_costs[0].bid_id', null);
    }
  }, [isSelectedBidEqualToFormCost, newCostSchema, setFieldValue]);

  const setCost = (value, form) => {
    if (newCostSchema) form.setFieldValue('quote_costs[0].cost.amount', value);
    form.setFieldValue('operation_details.cost.amount', value);
  };
  const setCurrency = (value, form) => {
    if (newCostSchema) form.setFieldValue('quote_costs[0].cost.currency', value);
    form.setFieldValue('operation_details.cost.currency', value);
  };
  const setComments = (event, form) => {
    if (newCostSchema) form.setFieldValue('quote_costs[0].comments', event.target.value);
    form.setFieldValue('operation_details.details', event.target.value);
  };
  const setCarrier = (value, form, selectedOption) => {
    const { value: carrier_id, label } = selectedOption;
    if (newCostSchema) {
      form.setFieldValue(`quote_costs[0].carrier.id`, carrier_id);
      form.setFieldValue(`quote_costs[0].carrier.name`, label);
    }
    form.setFieldValue(`operation_details.carrier`, label);
  };

  const onBidSelection = useCallback(
    (bid) => {
      const carrier = bid.bidder.carrier;
      const amount = bid.cost.amount;
      const currency = bid.cost.currency;
      setFieldValue(`operation_details.carrier`, carrier.name);
      setFieldValue('operation_details.cost.currency', currency);
      setFieldValue('operation_details.cost.amount', amount);
      if (newCostSchema) {
        setFieldValue(`quote_costs[0].carrier.name`, carrier.name);
        setFieldValue('quote_costs[0].cost.currency', currency);
        setFieldValue('quote_costs[0].cost.amount', amount);
        setFieldValue(`quote_costs[0].carrier.id`, carrier.id);
        setFieldValue('quote_costs[0].bid_id', bid.id);
      }
      setSelectedBid(bid);
    },
    [newCostSchema, setFieldValue]
  );
  return (
    <SectionContainer>
      <SectionTitle>{t.section_title}</SectionTitle>
      <CustomSpace />

      {showBids && (
        <QuoteBidSection
          onBidSelection={onBidSelection}
          selectedBid={selectedBid}
          bids={bids}
          isLoadingTicket={isLoadingTicket}
          ticketDetail={ticketDetail}
        />
      )}

      <SectionSubTitle>{t.carrier_subtitle}</SectionSubTitle>
      <SectionBody>
        <SectionLine>
          {carrierSelector && newCostSchema ? (
            <FormikPremiumInput
              onSearchChange={setSearch}
              onFieldChange={setCarrier}
              options={carrierOptionsWithExistingCarrier}
              isLoadingOptions={isLoadingCarrierOptions}
              customHasError={carrierOptionsError}
              initialValue={initialCarrierQuery}
              isRequired={true}
              showErrors={showErrors}
              label="Carrier"
              name="quote_costs[0].carrier.id"
              type="select-with-search"
              searchLabel={'Search carrier'}
              optionsEmptyMssg={'No carriers found'}
              isClereable={true}
              customErrorFieldName="operation_details.carrier"
            />
          ) : (
            <FormikPremiumInput
              isRequired={true}
              showErrors={showErrors}
              label="Carrier"
              name="operation_details.carrier"
              type="text"
            />
          )}
          {newCostSchema ? (
            <>
              <FormikPremiumInput
                showErrors={showErrors}
                label="Cost"
                isRequired={true}
                name="quote_costs[0].cost.amount"
                type={'number'}
                width={'160px'}
                onChange={setCost}
                min={0}
                customErrorFieldName="operation_details.cost.amount"
              />
              <FormikPremiumInput
                label="Currency"
                showErrors={showErrors}
                name="quote_costs[0].cost.currency"
                type={'select'}
                isRequired={true}
                width={'160px'}
                onChange={setCurrency}
                availableOptions={[
                  {
                    value: 'EUR',
                    label: 'Euro',
                  },
                  {
                    value: 'MAD',
                    label: 'Dirham',
                  },
                ]}
              />
            </>
          ) : (
            <>
              <FormikPremiumInput
                showErrors={showErrors}
                label="Cost"
                isRequired={true}
                name="operation_details.cost.amount"
                type={'number'}
                width={'160px'}
                min={0}
                onChange={setCost}
              />
              <FormikPremiumInput
                label="Currency"
                showErrors={showErrors}
                name="operation_details.cost.currency"
                type={'select'}
                onChange={setCurrency}
                isRequired={true}
                width={'160px'}
                availableOptions={[
                  {
                    value: 'EUR',
                    label: 'Euro',
                  },
                  {
                    value: 'MAD',
                    label: 'Dirham',
                  },
                ]}
              />
            </>
          )}
        </SectionLine>
        <SectionLine>
          {newCostSchema ? (
            <FormikPremiumInput
              showErrors={showErrors}
              label="Planning comments"
              name="quote_costs[0].comments"
              type="text"
              onChange={setComments}
            />
          ) : (
            <FormikPremiumInput
              showErrors={showErrors}
              label="Planning comments"
              name="operation_details.details"
              type="text"
            />
          )}
        </SectionLine>
      </SectionBody>
    </SectionContainer>
  );
};
